import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, message, Spin } from 'antd';
import { SubscriptionService } from '../../services/api/subscriptionService';
import { SubscriptionType } from '../../types/subscription';
import { CompanyService } from '../../services/api/companyService';
import { Company } from '../../types/company';

const { Option } = Select;

interface CreateSubscriptionDialogProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export const CreateSubscriptionDialog: React.FC<CreateSubscriptionDialogProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  useEffect(() => {
    if (visible) {
      loadData();
      form.resetFields();
    }
  }, [visible, form]);

  const loadData = async () => {
    setLoadingTypes(true);
    try {
      const [types, companiesList] = await Promise.all([
        SubscriptionService.getSubscriptionTypes(),
        CompanyService.getAll(),
      ]);
      setSubscriptionTypes(types.filter(type => type.is_active));
      setCompanies(companiesList);
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Failed to load subscription types');
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Find the subscription type by code to get its ID
      const subscriptionType = subscriptionTypes.find(
        type => type.code === values.subscription_type_code
      );

      if (!subscriptionType) {
        message.error('Subscription type not found');
        setLoading(false);
        return;
      }

      // Create company subscription using the company subscription endpoint
      await SubscriptionService.createCompanySubscription({
        company_id: values.company_id,
        subscription_type_id: subscriptionType.id,
        period_start_date: new Date().toISOString(),
        is_active: true,
      });

      message.success('Company subscription created successfully');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation errors
        return;
      }
      console.error('Error creating subscription:', error);
      message.error(error.message || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };


  return (
    <Modal
      title="Create Subscription"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="Create"
      cancelText="Cancel"
    >
      <Spin spinning={loadingTypes}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="subscription_type_code"
            label="Subscription Type"
            rules={[{ required: true, message: 'Please select a subscription type' }]}
          >
            <Select placeholder="Select subscription type" showSearch>
              {subscriptionTypes.map((type) => (
                <Option key={type.code} value={type.code}>
                  {type.name} ({type.code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="company_id"
            label="Company"
            rules={[{ required: true, message: 'Please select a company' }]}
          >
            <Select
              placeholder="Select company"
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

